import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Input, Popconfirm, Select, Space, Tag, Typography } from "antd";
import { EditOutlined, FileAddOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { Success } from "../../../../commons/Messages/Messages";
import { hasPermission } from "../../../../commons/Permissions/PermissionsHook";
import { UserContext } from "../../../../context/UserContext";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import PageHeader from "../shared/PageHeader";
import { formatDate } from "../shared/money";
import { CATEGORY_LABELS } from "../belege/constants";
import SelfBillingModal from "../belege/SelfBillingModal";
import SupplierModal from "./SupplierModal";
import { deactivateSupplier, getBelegeMeta, listSuppliers } from "./api";
import { SupplierComplianceBadge } from "../../subcontractors/ComplianceTag";

const MODULE = "Accounting";
const FREISTELLUNG = {
  valid: { color: "green", label: "Exemption valid" },
  expiring: { color: "orange", label: "Exemption expiring" },
  expired: { color: "red", label: "Exemption expired" },
  missing: { color: "red", label: "No exemption certificate" }
};

/** Kreditoren master data: list with search, create/edit modal, deactivate. */
const Suppliers = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const user = useContext(UserContext);
  const can = action => hasPermission(user?.permissions, MODULE, action);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("true");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ suppliers: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, supplier: null });
  const [selfBilling, setSelfBilling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listSuppliers({ q, active, page, limit: 25 });
      setData(res.data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBelegeMeta()
      .then(res => setMeta(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [q, active, page]);

  const deactivate = async supplier => {
    try {
      await deactivateSupplier(supplier._id);
      Success(t("Supplier deactivated"));
      load();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const columns = [
    { title: t("No."), dataIndex: "supplierNumber", width: 100, mobile: "subtitle" },
    {
      title: t("Name"),
      dataIndex: "name",
      ellipsis: true,
      mobile: "title",
      render: (value, row) => (
        <span className="acct-wrap">
          {value}
          {!row.active && <Tag style={{ marginInlineStart: 8 }}>{t("Inactive")}</Tag>}{" "}
          <SupplierComplianceBadge supplierId={row._id} />
        </span>
      )
    },
    { title: t("City"), dataIndex: ["address", "city"], width: 140, responsive: ["md"] },
    {
      title: t("Creditor account"),
      dataIndex: "kreditorAccount",
      width: 120,
      responsive: ["lg"],
      mobile: "hidden"
    },
    {
      title: t("Category"),
      dataIndex: "defaultCategory",
      width: 180,
      responsive: ["lg"],
      mobile: "hidden",
      render: value => (value ? t(CATEGORY_LABELS[value] || value) : "")
    },
    {
      title: t("Construction"),
      width: 200,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.is13bSupplier && <Tag color="blue">§ 13b</Tag>}
          {row.selfBillingAgreement?.agreed && <Tag color="geekblue">{t("Self-billing")}</Tag>}
          {FREISTELLUNG[row.freistellungStatus] && (
            <Tag color={FREISTELLUNG[row.freistellungStatus].color}>
              {t(FREISTELLUNG[row.freistellungStatus].label)}
            </Tag>
          )}
          {FREISTELLUNG[row.freistellungStatus] && row.bauabzugsteuer?.validUntil && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("Valid until")}{" "}
              <span className="acct-num">{formatDate(row.bauabzugsteuer.validUntil)}</span>
            </Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: "",
      key: "actions",
      width: 130,
      render: (_, row) => (
        <Space>
          {can("Create") && row.active && row.selfBillingAgreement?.agreed && (
            <Button
              size="small"
              icon={<FileAddOutlined />}
              aria-label={t("Create Gutschrift (self-billing)")}
              title={t("Create Gutschrift (self-billing)")}
              onClick={() => setSelfBilling(row)}
            >
              {isMobile ? t("Gutschrift") : null}
            </Button>
          )}
          {can("Update") && (
            <Button
              size="small"
              icon={<EditOutlined />}
              aria-label={t("Edit")}
              title={t("Edit")}
              onClick={() => setModal({ open: true, supplier: row })}
            >
              {isMobile ? t("Edit") : null}
            </Button>
          )}
          {can("Delete") && row.active && (
            <Popconfirm title={t("Deactivate this supplier?")} onConfirm={() => deactivate(row)}>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                aria-label={t("Deactivate")}
                title={t("Deactivate")}
              >
                {isMobile ? t("Deactivate") : null}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title={t("Suppliers")}
        extra={
          can("Create") ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModal({ open: true, supplier: null })}
            >
              {t("New supplier")}
            </Button>
          ) : null
        }
      />
      <PageToolbar
        filters={
          <>
            <Input.Search
              allowClear
              placeholder={t("Search name, number, IBAN, VAT ID")}
              onSearch={value => {
                setQ(value);
                setPage(1);
              }}
            />
            <Select
              value={active}
              onChange={value => {
                setActive(value);
                setPage(1);
              }}
              options={[
                { value: "true", label: t("Active") },
                { value: "false", label: t("Inactive") },
                { value: "all", label: t("All") }
              ]}
            />
          </>
        }
      />
      <Card size="small">
        <ResponsiveTable
          rowKey="_id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data.suppliers}
          pagination={{
            current: page,
            pageSize: 25,
            total: data.total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>
      <SupplierModal
        open={modal.open}
        supplier={modal.supplier}
        categories={meta?.categories}
        accounts={meta?.accounts}
        bauleistender={meta?.settings?.bauleistender}
        onClose={() => setModal({ open: false, supplier: null })}
        onSaved={() => {
          setModal({ open: false, supplier: null });
          load();
        }}
      />
      <SelfBillingModal
        open={Boolean(selfBilling)}
        supplier={selfBilling}
        onClose={() => setSelfBilling(null)}
        onDone={() => setSelfBilling(null)}
      />
    </div>
  );
};

export default Suppliers;
